import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Boxes,
  Coins,
  Home,
  Receipt,
  ScrollText,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleProvider";
import { REPORT_CENTER_ITEMS, REPORT_CATEGORIES } from "@/lib/reportCenterNav";
import { isMultiBranchFeatureEnabled } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import "@/styles/simple-ui.css";

const FEATURED = [
  { id: "z-report", labelKey: "nav.zReport", hintKey: "simple.reportsDayCloseHint", icon: ScrollText, bg: "bg-[#DC2626]", path: "/reports/z-report" },
  { id: "x-report", labelKey: "nav.xReport", hintKey: "simple.reportsXHint", icon: ScrollText, bg: "bg-[#EA580C]", path: "/reports/x-report" },
  { id: "daily-sales", label: "Daily Sales", hintKey: "simple.reportsSalesHint", icon: BarChart3, bg: "bg-[#2563EB]", path: "/reports/daily-sales" },
  { id: "bill-history", label: "Bill History", hintKey: "simple.reportsBillsHint", icon: Receipt, bg: "bg-[#7C3AED]", path: "/reports/bill-history" },
  { id: "cash-collection", label: "Cash Collection", hintKey: "simple.reportsCashHint", icon: Coins, bg: "bg-[#059669]", path: "/reports/cash-collection" },
  { id: "top-items", label: "Top Items", hintKey: "simple.reportsTopHint", icon: TrendingUp, bg: "bg-[#D97706]", path: "/reports/top-items" },
];

const CAT_COLORS = {
  Sales: "bg-blue-600",
  Customers: "bg-violet-600",
  Suppliers: "bg-amber-600",
  Operations: "bg-teal-600",
  Accounting: "bg-slate-700",
  Inventory: "bg-orange-600",
};

export default function SimpleReportsHub() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const { user } = useAuth();
  const [filter, setFilter] = useState("All");
  const multiBranch = isMultiBranchFeatureEnabled(user);

  const catalog = useMemo(() => {
    let items = REPORT_CENTER_ITEMS;
    if (!multiBranch) items = items.filter((r) => r.id !== "branch-comparison");
    return items;
  }, [multiBranch]);

  const visible = useMemo(() => {
    if (filter === "All") return catalog;
    return catalog.filter((r) => r.category === filter);
  }, [catalog, filter]);

  return (
    <div className="simple-ui flex h-full flex-col" dir={isRtl ? "rtl" : "ltr"} data-testid="simple-reports-hub">
      <div className="flex items-center gap-2 border-b bg-white px-3 py-2">
        <Link to="/" className="inline-flex h-10 items-center gap-1 rounded-xl bg-violet-50 px-3 text-sm font-bold text-violet-700">
          <Home className="h-4 w-4" />
          {t("common.home")}
        </Link>
        <h1 className="text-lg font-extrabold text-slate-900">{t("nav.reports")}</h1>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <h2 className="mb-3 text-sm font-extrabold uppercase tracking-wide text-slate-500">
          {t("simple.reportsFeatured", "Day close & cash")}
        </h2>
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {FEATURED.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.id}
                to={item.path}
                className={cn("simple-tile flex min-h-[110px] flex-col items-center justify-center gap-2 p-3 text-center text-white", item.bg)}
                data-testid={`simple-report-${item.id}`}
              >
                <Icon className="h-7 w-7" strokeWidth={2.25} />
                <span className="text-[13px] font-extrabold leading-tight">
                  {item.labelKey ? t(item.labelKey) : item.label}
                </span>
                <span className="text-[10px] font-medium text-white/85">{t(item.hintKey)}</span>
              </Link>
            );
          })}
        </div>

        <h2 className="mb-2 text-sm font-extrabold uppercase tracking-wide text-slate-500">
          {t("simple.reportsAll", "All reports")}
        </h2>
        <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
          {REPORT_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFilter(c)}
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-xs font-bold",
                filter === c ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600",
              )}
            >
              {c === "All" ? t("simple.posAll", "All") : c}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((r) => {
            const Icon = r.icon || Boxes;
            return (
              <Link
                key={r.id}
                to={r.path}
                className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm hover:border-violet-200"
              >
                <span className={cn("grid h-11 w-11 place-items-center rounded-xl text-white", CAT_COLORS[r.category] || "bg-slate-500")}>
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-extrabold text-slate-900">{r.label}</div>
                  <div className="text-[11px] font-semibold text-slate-400">{r.category}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
